import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { peopleWebSocketService } from '../services/ws';
import { listStreams as listStream } from '../services/api';
import { getPeopleCountHistoryCombined } from '../services/api';
import { useAuth0 } from '@auth0/auth0-react';
import { IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonIcon, IonContent, IonPage } from '@ionic/react';
import { videocamOutline, peopleOutline, trendingUpOutline } from 'ionicons/icons';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import CameraCardButton from '../components/CameraCardButton';

// Usando a estrutura real dos dados
type CameraData = {
  time: string; // HH:MM:SS
  count: number; // Número de pessoas
  history: { time: string; count: number }[];
};

// Tipo para dados do WebSocket (corrigido)
type PeopleRealtimeData = {
  time: string; // Já vem como "HH:MM:SS"
  count: number; // Número de pessoas
  stream_id?: string; // ID do stream (opcional)
};

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const Home: React.FC = () => {
  const [streamList, setStreamList] = useState<any[]>([]);
  const [realtimeData, setRealtimeData] = useState<Record<string, CameraData>>({});
  const { getAccessTokenSilently } = useAuth0();
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const data = await listStream(token);
        setStreamList(data);
      } catch {
        setStreamList([]);
      }
    })();
  }, [getAccessTokenSilently, location]);

  // Recebe dados em tempo real do WebSocket
  useEffect(() => {
    if (!streamList.length) return;

    streamList.forEach(async (stream) => {
      try {
        const token = await getAccessTokenSilently();
        const history = await getPeopleCountHistoryCombined(stream.id, token);

        setRealtimeData(prev => ({
          ...prev,
          [stream.id]: {
            time: history.length ? history[history.length - 1].time : '--:--',
            count: history.length ? history[history.length - 1].count : 0,
            history: history.map((item: any) => ({
              time: item.time, // Já vem como string das APIs
              count: item.count
            }))
          }
        }));

        // WebSocket connection - SIMPLIFICADO
        peopleWebSocketService.connect(stream.id, (data: PeopleRealtimeData) => {
          setRealtimeData(prev => {
            const prevHistory = prev[stream.id]?.history || [];

            // Só adiciona se for um frame novo
            if (
              prevHistory.length === 0 ||
              data.time !== prevHistory[prevHistory.length - 1].time
            ) {
              return {
                ...prev,
                [stream.id]: {
                  time: data.time, // Usa diretamente - já é "HH:MM:SS"
                  count: data.count,
                  history: [
                    ...prevHistory.slice(-2999),
                    { time: data.time, count: data.count }
                  ]
                }
              };
            }
            return prev;
          });
        });
      } catch (e) {
        console.error('Erro ao carregar dados:', e);
      }
    });

    return () => {
      peopleWebSocketService.disconnectAll();
    };
  }, [streamList, getAccessTokenSilently]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Histórico de Pessoas',
        font: { size: 14, weight: 'bold' as const },
        color: '#222'
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#3880ff',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function (context: any) {
            return `Horário: ${context[0].label}`;
          },
          label: function (context: any) {
            return `Pessoas: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 5, // ← Mantendo as configurações originais do eixo X
          font: {
            size: 10
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0,0,0,0.1)',
        },
        ticks: {
          stepSize: 1,
          precision: 0,
          font: {
            size: 10
          },
          callback: function (value: string | number) {
            if (typeof value === "number" && Number.isInteger(value)) {
              return value;
            }
            return null;
          }
        }
      }
    },
    elements: {
      point: {
        radius: 2, // ← Pontos menores para melhor visualização com muitos dados
        hoverRadius: 5,
      },
      line: {
        tension: 0.4,
        borderJoinStyle: 'round' as const,
        borderCapStyle: 'round' as const,
        spanGaps: true
      }
    }
  };

  // Calcular total de pessoas em todas as câmeras - usando 'count'
  const totalPeople = Object.values(realtimeData).reduce((sum, cam) => sum + cam.count, 0);
  const activeCameras = streamList.length;

  const optimizeChartData = (history: { time: string; count: number }[]) => {
    if (history.length <= 50) {
      // Se há poucos dados, mostra todos
      return history;
    }

    // Se há muitos dados, reduz para melhor performance visual
    const result = [];
    const step = Math.ceil(history.length / 100); // Máximo 100 pontos

    for (let i = 0; i < history.length; i += step) {
      result.push(history[i]);
    }

    // Sempre inclui o último ponto para mostrar estado atual
    if (result[result.length - 1] !== history[history.length - 1]) {
      result.push(history[history.length - 1]);
    }

    return result;
  };

  return (
    <IonPage>
      <IonContent
        fullscreen
        style={{
          '--background': 'linear-gradient(225deg, #eaf1fb 60%, #f6f8fa 100%)'
        }}
        scrollEvents={true}
      >
        <div style={{
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          padding: '16px',
          paddingBottom: '32px'
        }}>
          {/* SVG gradiente decorativo */}
          <svg
            width="320"
            height="200"
            viewBox="0 0 320 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              zIndex: 0,
              pointerEvents: 'none',
              maxWidth: '80vw'
            }}
          >
            <defs>
              <radialGradient id="rightGradient" cx="0.8" cy="0.2" r="1">
                <stop offset="0%" stopColor="#3880ff" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#eaf1fb" stopOpacity="0" />
              </radialGradient>
            </defs>
            <ellipse cx="200" cy="40" rx="140" ry="40" fill="url(#rightGradient)" />
          </svg>

          {/* Header com logo e estatísticas */}
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: '20px',
            marginBottom: 20,
            boxShadow: '0 2px 16px 0 rgba(56,128,255,0.08)',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 16
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 auto' }}>
                <img src="/favicon.png" alt="Trackento Logo" style={{ width: 40, height: 40 }} />
                <div style={{ minWidth: 0 }}>
                  <h1 style={{
                    fontSize: 'clamp(20px, 5vw, 28px)',
                    fontWeight: 700,
                    margin: 0,
                    color: '#222',
                    letterSpacing: -0.3,
                    lineHeight: 1.2
                  }}>
                    Painel de Monitoramento
                  </h1>
                  <p style={{
                    fontSize: 'clamp(14px, 3.5vw, 16px)',
                    color: '#666',
                    margin: 0,
                    marginTop: 4
                  }}>
                    Dados em tempo real das suas câmeras
                  </p>
                </div>
              </div>

              {/* Cards de estatísticas */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #3880ff 0%, #2260e8 100%)',
                  color: '#fff',
                  padding: '10px 16px',
                  borderRadius: 12,
                  textAlign: 'center',
                  minWidth: 100
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 2 }}>
                    <IonIcon icon={peopleOutline} style={{ fontSize: 18 }} />
                    <span style={{ fontSize: 22, fontWeight: 700 }}>{totalPeople}</span>
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.9 }}>Total de Pessoas</div>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #10dc60 0%, #0fb653 100%)',
                  color: '#fff',
                  padding: '10px 16px',
                  borderRadius: 12,
                  textAlign: 'center',
                  minWidth: 100
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 2 }}>
                    <IonIcon icon={videocamOutline} style={{ fontSize: 18 }} />
                    <span style={{ fontSize: 22, fontWeight: 700 }}>{activeCameras}</span>
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.9 }}>Câmeras Ativas</div>
                </div>
              </div>
            </div>
          </div>

          {/* Grid de câmeras */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(350px, 100%), 1fr))',
            gap: 16,
            position: 'relative',
            zIndex: 1
          }}>
            {streamList.map((stream) => {
              const camData = realtimeData[stream.id];
              return (
                <IonCard key={stream.id} style={{
                  borderRadius: 16,
                  boxShadow: '0 4px 20px 0 rgba(56,128,255,0.12)',
                  border: 'none',
                  overflow: 'hidden',
                  margin: 0
                }}>
                  <IonCardHeader style={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #fff 100%)',
                    borderBottom: '1px solid #eee',
                    padding: 16
                  }}>
                    <IonCardTitle style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      fontSize: 16,
                      fontWeight: 700,
                      color: '#222'
                    }}>
                      <div style={{
                        background: '#3880ff',
                        borderRadius: 8,
                        padding: 6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <IonIcon icon={videocamOutline} style={{ fontSize: 18, color: '#fff' }} />
                      </div>
                      <span style={{ minWidth: 0, wordBreak: 'break-word' }}>{stream.camera_name}</span>
                    </IonCardTitle>
                  </IonCardHeader>

                  <IonCardContent style={{ padding: 16 }}>
                    {camData ? (
                      <>
                        {/* Métricas em tempo real */}
                        <div style={{
                          display: 'flex',
                          gap: 12,
                          marginBottom: 20,
                          flexWrap: 'wrap'
                        }}>
                          <div style={{
                            background: 'linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)',
                            padding: 14,
                            borderRadius: 12,
                            flex: 1,
                            minWidth: 120,
                            textAlign: 'center',
                            border: '1px solid #e1e8ed'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 6 }}>
                              <IonIcon icon={peopleOutline} style={{ fontSize: 22, color: '#3880ff' }} />
                              <span style={{ fontSize: 24, fontWeight: 700, color: '#222' }}>{camData.count}</span>
                            </div>
                            <div style={{ fontSize: 12, color: '#666', fontWeight: 500 }}>Pessoas Agora</div>
                          </div>

                          <div style={{
                            background: 'linear-gradient(135deg, #f3e5f5 0%, #f8f9fa 100%)',
                            padding: 14,
                            borderRadius: 12,
                            flex: 1,
                            minWidth: 120,
                            textAlign: 'center',
                            border: '1px solid #e1e8ed'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 6 }}>
                              <IonIcon icon={trendingUpOutline} style={{ fontSize: 22, color: '#9c27b0' }} />
                              <span style={{ fontSize: 16, fontWeight: 700, color: '#222' }}>
                                {camData.time}
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: '#666', fontWeight: 500 }}>Última Atualização</div>
                          </div>
                        </div>

                        {/* Gráfico */}
                        <div style={{
                          background: '#fff',
                          borderRadius: 12,
                          padding: 12,
                          border: '1px solid #eee',
                          marginBottom: 16
                        }}>
                          <div style={{ width: '100%', height: 250 }}>
                            <Line
                              data={{
                                labels: optimizeChartData(camData.history).map((item) => item.time),
                                datasets: [
                                  {
                                    label: 'Pessoas',
                                    data: optimizeChartData(camData.history).map((item) => item.count),
                                    fill: true,
                                    backgroundColor: 'rgba(56, 128, 255, 0.1)',
                                    borderColor: '#3880ff',
                                    borderWidth: 2,
                                    tension: 0.4,
                                    pointBackgroundColor: '#3880ff',
                                    pointBorderColor: '#fff',
                                    pointBorderWidth: 2,
                                    pointRadius: camData.history.length > 50 ? 1 : 3,
                                    spanGaps: true,
                                    showLine: true,
                                    borderJoinStyle: 'round',
                                    borderCapStyle: 'round',
                                  },
                                ],
                              }}
                              options={{
                                ...chartOptions,
                                elements: {
                                  ...chartOptions.elements,
                                  point: {
                                    radius: camData.history.length > 50 ? 1 : 3,
                                    hoverRadius: camData.history.length > 50 ? 4 : 6,
                                  }
                                }
                              }}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        padding: 30,
                        color: '#666',
                        fontSize: 14
                      }}>
                        <IonIcon icon={videocamOutline} style={{ fontSize: 40, opacity: 0.3, marginBottom: 12 }} />
                        <div>Aguardando dados em tempo real...</div>
                      </div>
                    )}

                    <CameraCardButton streamId={stream.id} cameraName={stream.camera_name} />
                  </IonCardContent>
                </IonCard>
              );
            })}
          </div>

          {/* Mensagem quando não há câmeras */}
          {streamList.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: 40,
              background: '#fff',
              borderRadius: 16,
              boxShadow: '0 2px 16px 0 rgba(56,128,255,0.08)',
              position: 'relative',
              zIndex: 1
            }}>
              <IonIcon icon={videocamOutline} style={{ fontSize: 48, opacity: 0.3, marginBottom: 16, color: '#3880ff' }} />
              <h3 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px 0', color: '#222' }}>
                Nenhuma câmera configurada
              </h3>
              <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
                Configure suas câmeras para começar a monitorar o fluxo de pessoas
              </p>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Home;